import React, { useEffect, useState } from "react";
import {
  Upload,
  FileText,
  Download,
  CheckCircle,
  Loader,
  Loader2,
  Info,
  InfoIcon,
} from "lucide-react";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import Seo from "../../Components/SeoComponent/Seo";
import Header from "../../Components/Header";
import { fetchProfile } from "../../Services/profile/profileservice";
import {
  downloadBulkRc,
  uploadBulkRc,
} from "../../Services/ApiTestService/apiTestService";
import { Link } from "react-router-dom";
import { CgSandClock } from "react-icons/cg";

function BulkRcToMobile() {
  const [file, setFile] = useState(null);
  const [isUploaded, setIsUploaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [uploadResponse, setUploadResponse] = useState(null);
  const [referenceId, setReferenceId] = useState(null);
  const [downloadResponse, setDownloadResponse] = useState(null);
  const [isDownloadAvailable, setIsDownloadAvailable] = useState(false);
  const [isProcessInitiated, setIsProcessInitiated] = useState(false);

  const [isProcessFinished, setIsProcessFinished] = useState(false);

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setFile(e.target.files[0]);
      setIsUploaded(false); // Reset status if a new file is picked
    }
  };

  const handleUpload = async () => {
    if (!file) return showErrorToast("Please select a file first!");
    // Logic for processing the file would go here

    try {
      setLoading(true);

      const res = await uploadBulkRc({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        file,
      });

      if (res) {
        setUploadResponse(res);
        console.log(res);
      }

      if (res?.status?.code === 200) {
        setIsUploaded(true);
        setIsProcessInitiated(true);
        setReferenceId(res?.data?.reference_id);
        showSuccessToast(res?.message || "Success");
      } else if (res?.status?.code === 422) {
        showErrorToast(res?.data?.message || "invalid details ");
      } else if (res?.status?.code === 500) {
        showErrorToast(res?.data?.message || "server error");
      } else if (!res.success) {
        showErrorToast(res?.data?.message || "verificaton failed");
      } else {
        showErrorToast(res?.data?.message || "something went wrong");
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Server error");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (refId) => {
    try {
      const res = await downloadBulkRc({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        reference_id: refId,
      });

      if (res) {
        setDownloadResponse(res);
        console.log(res);
      }

      if (res?.status?.code === 200) {
        if (res?.data?.file) {
          setIsProcessFinished(true);
          setIsProcessInitiated(false);
          setIsDownloadAvailable(true);
          showSuccessToast(res?.message || "Success");
        }
      } else if (res?.status?.code === 422) {
        showErrorToast(res?.data?.message || "invalid details ");
      } else if (res?.status?.code === 500) {
        showErrorToast(res?.data?.message || "server error");
      } else if (!res.success) {
        showErrorToast(res?.data?.message || "verificaton failed");
      } else {
        showErrorToast(res?.data?.message || "something went wrong");
      }
    } catch (error) {
      console.log(error);
    }
  };

  // ---------------- FETCH PROFILE ----------------
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      }
    };

    loadProfile();
  }, []);

  useEffect(() => {
    console.log("reference_id from api", referenceId);
  }, [referenceId]);

  useEffect(() => {
    if (isDownloadAvailable || !referenceId) return;

    const interval = setInterval(() => {
      handleDownload(referenceId);
    }, 25000);

    return () => {
      clearInterval(interval);
    };
  }, [referenceId, isDownloadAvailable]);

  return (
    <div className="min-h-screen">
      <Seo />
      <Header heading={"Challan Pro"} />

      <div className=" bg-gray-50 flex items-start justify-center sm:px-6 font-sans">
        <div className="w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          {/* Header & Instructions */}
          <div className="text-center mb-8 ">
            <h2 className="text-2xl font-bold text-gray-800 mb-2 flex justify-between items-center">
              Bulk RC to Mobile
              <a
                className="items-center rounded-md underline flex text-green-600 text-[16px] gap-3"
                href="sample rc.xlsx"
                download
              >
                <Download size={20} /> Download Sample File
              </a>
            </h2>
            <p className="text-gray-500 text-sm leading-relaxed">
              Please upload your{" "}
              <span className="font-semibold text-blue-600">
                CSV, xls, xlsx file
              </span>{" "}
              below. Once processed, you will be able to download the response
              details immediately.
            </p>
          </div>

          {/* Upload Area */}
          <div className="space-y-6">
            <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 hover:border-blue-300 transition-all">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <Upload className="w-10 h-10 text-gray-400 mb-3" />
                <p className="text-sm text-gray-600">
                  {file ? (
                    <span className="font-medium text-green-600">
                      {file.name}
                    </span>
                  ) : (
                    "Click to upload or drag and drop"
                  )}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  CSV, xls, xlsx files only
                </p>
              </div>
              <input
                type="file"
                className="hidden"
                accept=".csv,.xls,.xlsx"
                onChange={handleFileChange}
              />
            </label>

            {/* Action Button */}
            {!isProcessFinished ? (
              <button
                onClick={handleUpload}
                disabled={!file || loading || isProcessInitiated}
                className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all shadow-md flex items-center justify-center gap-2 ${
                  file
                    ? "bg-blue-600 hover:bg-blue-700 active:scale-95"
                    : "bg-gray-300 cursor-not-allowed"
                }`}
              >
                <FileText size={18} />
                {loading ? (
                  <div className="flex items-center gap-2 justify-center">
                    <Loader2 className="animate-spin" /> Uploading...
                  </div>
                ) : !isDownloadAvailable && isProcessInitiated ? (
                  <div className="flex items-center gap-2 justify-center">
                    <CgSandClock className="animate-spin" /> Please Wait, We are
                    processing your file.
                  </div>
                ) : (
                  "Process File"
                )}
              </button>
            ) : (
              <div className="space-y-3 animate-in fade-in zoom-in duration-300">
                <div className="flex items-center gap-2 text-green-600 justify-center text-sm font-medium">
                  <CheckCircle size={16} />
                  Processing Complete!
                </div>
                <a
                  download
                  href={
                    isDownloadAvailable && downloadResponse?.data?.file
                      ? downloadResponse?.data?.file
                      : undefined
                  }
                  className="w-full py-3 px-4 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-all shadow-md flex items-center justify-center gap-2 active:scale-95 cursor-pointer"
                >
                  <Download size={18} />
                  {isDownloadAvailable
                    ? " Download File"
                    : "Wait While File Get Available"}
                </a>
              </div>
            )}
          </div>

          {isProcessInitiated && (
            <div className="flex items-center justify-around  p-4 mt-4 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-100 rounded-xl ">
              <Info size={20} className="shrink-0 animate-ping" />
              <p>
                Please do not refresh the page until your file is ready for
                download.
              </p>
              <Info size={20} className="shrink-0 animate-ping" />
            </div>
          )}

          {/* Footer Note */}
          <div className="mt-8 flex items-center justify-center gap-2 pt-6 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-400">
              Ensure your CSV follows the standard RC format.
            </p>

             <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 px-3 py-2 rounded-md">
                          <Info className="w-4 h-4" />
                          <span>Max 200 records allowed in a single File</span>
                        </div>
          </div>
        </div>

       
      </div>

       <div className="flex justify-center w-full mt-4">
                  <div className="flex flex-wrap justify-center p-4 rounded-lg md:rounded-full border gap-2 border-green-400 font-semibold bg-green-50 text-green-700 ">
            <InfoIcon className="ml-4" />
            file processing will take 5 - 10 minutes or depends on the number of
            records, processed file report can be download from{" "}
            <a
              className="underline text-blue-600 font-bold"
              href="/BulkReportAll"
            >
              {" "}
              Report Page
            </a>
          </div>
        </div>
    </div>
  );
}

export default BulkRcToMobile;
